import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Col, Row } from 'antd'
import { Formik } from 'formik'
import moment from 'moment'
import * as Yup from 'yup'

import { ProductIds } from 'core/application/commons/config'
import {
  CreateCompanyForTestingRequest,
  ProductClassCategoryAllowanceRequest,
  ProductClassFamilyAllowanceRequest,
  createCompanyForTesting,
} from 'core/application/companies/createCompanyForTesting'
import { getDataForCreateCompanyForTesting } from 'core/application/companies/getDataForCreateCompanyForTesting'
import { Product, ProductAllowance } from 'core/domain/product/Product'

import DatePicker from 'common/components/dataEntry/formik/FormikDatePickerField'

import Card from '../../common/components/dataDisplay/Card'
import Label from '../../common/components/dataEntry/components/Label'
import Option from '../../common/components/dataEntry/components/Option'
import { Checkbox, Select } from '../../common/components/dataEntry/formik'
import Input from '../../common/components/dataEntry/formik/FormikDefaultInputField'
import Radio from '../../common/components/dataEntry/formik/FormikRadioField'
import notification from '../../common/components/feedback/Notification'
import Button from '../../common/components/general/Button'
import useCommandRequest from '../../common/hooks/useCommandRequest'
import useQueryRequest from '../../common/hooks/useQueryRequest'

import './styles.less'

export const QA_UTILS_PATH = 'qa-utils'

const QaUtils: React.FC = () => {
  const { t: translate } = useTranslation()

  const [initialValues, setInitialValues] = useState<CreateCompanyForTestingRequest>({
    email: '',
    benefitStartDate: moment().add(1, 'M').toDate(),
    benefitEndDate: moment().add(13, 'M').toDate(),
    name: '',
    numberOfEligibleEmployees: 0,
    numberOfIneligibleEmployees: 0,
    productId: Product.GroupHraId,
    allowanceType: 0,
    productClasses: [],
    isAle: false,
    numberOfNotAttestedEmployees: 0,
  })

  const [sendCommand, isLoading] = useCommandRequest()
  const [dataForTesting] = useQueryRequest(null, () => getDataForCreateCompanyForTesting())

  useEffect(() => {
    if (dataForTesting) {
      setInitialValues({
        ...initialValues,
        productClasses: [
          {
            classTypeId: 8,
            waitingPeriod: 0,
            categoryAllowances: dataForTesting.categories.map<ProductClassCategoryAllowanceRequest>((x) => {
              return {
                categoryId: x.id,
                amount: 1000,
              }
            }),
            familyAllowances: dataForTesting.availableFamilyStatuses.map<ProductClassFamilyAllowanceRequest>((x) => {
              return {
                familyStatusId: x.id,
                amount: 1000,
              }
            }),
          },
        ],
      })
    }
  }, [dataForTesting])

  const submitForm = async (values: CreateCompanyForTestingRequest) => {
    // const token = await getCreditCardToken()
    const request: CreateCompanyForTestingRequest = {
      ...values,
      // paymentToken: token,
    }

    const result = await sendCommand(() => createCompanyForTesting(request))

    if (result.completed) {
      notification.open({
        message: 'Company created successfully. ',
        type: 'success',
      })

      setTimeout(() => {
        window.location.reload()
      }, 1000)
    }
  }

  const getAllowanceType = (productId?: number): ProductAllowance | null => {
    if (!productId || !dataForTesting) return null

    return dataForTesting.products.find((x) => x.id === productId)?.allowance
  }

  return (
    <>
      <Formik
        enableReinitialize={true}
        validationSchema={formValidator(translate)}
        initialValues={initialValues}
        onSubmit={(values) => {}}
      >
        {(formik) => (
          <form onSubmit={formik.handleSubmit} autoComplete="off" id="recurly-form">
            <Card title={'Add company'} style={{ maxWidth: '760px' }}>
              <Row>
                <Col span={24}>
                  <Row gutter={16} className={'mb-16'}>
                    <Col span={24}>
                      <Input label={'Company Name'} name="name"></Input>
                    </Col>
                  </Row>

                  <Row gutter={16} className={'mb-16'}>
                    <Col span={24}>
                      <Input label={'Admin email'} name="email"></Input>
                    </Col>
                  </Row>

                  <Row gutter={16} className={'mb-16'}>
                    <Col span={24}>
                      <div>
                        <Label htmlFor={'benefitStartDate'}>Benefit start date</Label>
                      </div>
                      <DatePicker name="benefitStartDate" id="benefitStartDate" placeholder={'Benefit start date'} />
                    </Col>
                  </Row>

                  <Row gutter={16} className={'mb-16'}>
                    <Col span={24}>
                      <div>
                        <Label htmlFor={'benefitEndDate'}>Benefit end date</Label>
                      </div>
                      <DatePicker name="benefitEndDate" id="benefitEndDate" placeholder={'Benefit end date'} />
                    </Col>
                  </Row>

                  <Row gutter={16} className={'mb-16'}>
                    <Col span={24}>
                      <Input
                        label={'Number Of Eligible Employees'}
                        name="numberOfEligibleEmployees"
                        type="number"
                      ></Input>
                    </Col>
                  </Row>

                  <Row gutter={16} className={'mb-16'}>
                    <Col span={24}>
                      <Input
                        label={'Number Of Ineligible Employees'}
                        name="numberOfIneligibleEmployees"
                        type="number"
                      ></Input>
                    </Col>
                  </Row>

                  <Row gutter={16} className={'mb-16'}>
                    <Col span={24}>
                      <div>Product</div>
                      <Radio.Group name="productId" className="w-100" defaultValue={formik.values.productId}>
                        <Row gutter={10}>
                          {dataForTesting &&
                            dataForTesting.products.map((x) => (
                              <Col span={24} className={'mb-8'}>
                                <Radio.Button value={x.id}>{x.name}</Radio.Button>
                              </Col>
                            ))}
                        </Row>
                      </Radio.Group>
                    </Col>
                  </Row>

                  {formik.values.productId !== ProductIds.Perks && (
                    <Row gutter={16} className={'mb-16'}>
                      <Col span={24}>
                        <div>Allowance Type</div>
                        <Radio.Group name="allowanceType" className="w-100" defaultValue={formik.values.allowanceType}>
                          <Row gutter={10}>
                            <Col span={24} className={'mb-8'}>
                              <Radio.Button value={0}>Monthly</Radio.Button>
                            </Col>
                          </Row>
                          <Row gutter={10}>
                            <Col span={24} className={'mb-8'}>
                              <Radio.Button value={1}>Annual</Radio.Button>
                            </Col>
                          </Row>
                        </Radio.Group>
                      </Col>
                    </Row>
                  )}

                  {formik.values.productId === ProductIds.IchraId && (
                    <Row gutter={10} className={'mb-8 mt-8'}>
                      <Col span={8} className={'mb-8 mt-24'}>
                        <Checkbox name={'isAle'} label={'Is ALE'}>
                          Is ALE
                        </Checkbox>
                      </Col>
                      <Col span={16} className={'mb-8 mt-8'}>
                        <Input
                          label={'Number Of Not Attested Employees'}
                          name="numberOfNotAttestedEmployees"
                          type="number"
                          defaultValue={'0'}
                        ></Input>
                      </Col>
                    </Row>
                  )}

                  <div className={'products-wrap'}>
                    {formik.values.productClasses.map((x, index) => (
                      <div className={'product-single-class-wrap'}>
                        <div className={'product-single-class-select'}>
                          <Select
                            label={'Class'}
                            name={`productClasses[${index}].classTypeId`}
                            placeholder={'Choose a class'}
                          >
                            {dataForTesting &&
                              dataForTesting.availableClasTypes.map((item) => (
                                <Option key={item.id} value={item.id}>
                                  {item.name}
                                </Option>
                              ))}
                          </Select>
                        </div>

                        {ProductAllowance.Family === getAllowanceType(formik.values.productId) && (
                          <div className={'product-single-class-family-statuses-wrap'}>
                            {dataForTesting &&
                              dataForTesting.availableFamilyStatuses.map((item, familyIndex) => (
                                <div>
                                  <Input
                                    label={item.name}
                                    name={`productClasses[${index}].familyAllowances[${familyIndex}].amount`}
                                    type="number"
                                  ></Input>
                                  <Input
                                    label={''}
                                    name={`productClasses[${index}].familyAllowances[${familyIndex}].familyStatusId`}
                                    defaultValue={item.id.toString()}
                                    type="hidden"
                                  ></Input>
                                </div>
                              ))}
                          </div>
                        )}

                        {ProductAllowance.Category === getAllowanceType(formik.values.productId) && (
                          <div className={'product-single-class-family-statuses-wrap'}>
                            {dataForTesting &&
                              dataForTesting.categories.map((item, categoryIndex) => (
                                <div>
                                  <Input
                                    label={item.name}
                                    name={`productClasses[${index}].categoryAllowances[${categoryIndex}].amount`}
                                    type="number"
                                  ></Input>
                                  <Input
                                    label={''}
                                    name={`productClasses[${index}].categoryAllowances[${categoryIndex}].categoryId`}
                                    defaultValue={item.id.toString()}
                                    type="hidden"
                                  ></Input>
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                    ))}

                    <Button
                      type="default"
                      htmlType="button"
                      onClick={() => {
                        const actual = {
                          ...formik.values,
                        }

                        actual.productClasses.push({
                          classTypeId: 8,
                          waitingPeriod: 0,
                          categoryAllowances: dataForTesting.categories.map<ProductClassCategoryAllowanceRequest>(
                            (x) => {
                              return {
                                categoryId: x.id,
                                amount: 1000,
                              }
                            },
                          ),
                          familyAllowances:
                            dataForTesting.availableFamilyStatuses.map<ProductClassFamilyAllowanceRequest>((x) => {
                              return {
                                familyStatusId: x.id,
                                amount: 1000,
                              }
                            }),
                        })

                        setInitialValues(actual)
                      }}
                    >
                      Add another class
                    </Button>
                  </div>
                </Col>
              </Row>

              {/*<CreditCardPayment recurlyErrors={creditCardErrors} hasDirtyData={setHasDirtyData} />*/}

              <div className="mt-16">
                <Button
                  onClick={() => submitForm(formik.values)}
                  type="primary"
                  loading={isLoading}
                  disabled={!formValidator(translate).isValidSync(formik.values) || isLoading}
                >
                  {translate('general.save')}
                </Button>
              </div>
            </Card>
          </form>
        )}
      </Formik>
    </>
  )
}

const formValidator = (translate: any) =>
  Yup.object({
    name: Yup.string().nullable().required(translate('general.required')),
    email: Yup.string().nullable().email().required(translate('general.required')),
    benefitStartDate: Yup.date().nullable().required(translate('general.required')),
    benefitEndDate: Yup.date().nullable().required(translate('general.required')),
    numberOfEligibleEmployees: Yup.number()
      .max(30, 'Number of eligible employees must be less than or equal to 30')
      .min(0, 'Number of eligible employees must be greater than or equal to 0')
      .required(translate('general.required')),
    numberOfIneligibleEmployees: Yup.number()
      .max(30, 'Number of ineligible employees must be less than or equal to 30')
      .min(0, 'Number of ineligible employees must be greater than or equal to 0')
      .required(translate('general.required')),
    productId: Yup.number().max(4).min(1),
  })

export default QaUtils
